import Component from "@glimmer/component";
import { arg, forbidExtraArgs } from "glesys-controlpanel/utils/arg";
import { bool, instanceOf } from "prop-types";
import { service } from "@ember/service";
import ShipmentModel from "glesys-controlpanel/models/shipment";
import dayjs from "dayjs";
import Box from "glesys-controlpanel/components/box";
import EntityTable from "glesys-controlpanel/components/entity/table";
import Pill from "glesys-controlpanel/components/pill";
import PillInfo from "glesys-controlpanel/components/pill/info";
import PillSuccess from "glesys-controlpanel/components/pill/success";
import PillError from "glesys-controlpanel/components/pill/error";
import IconsPackage from "glesys-controlpanel/components/icons/package";
import EntityRemoteHandsRequest from "glesys-controlpanel/components/entity/remote-hands-request";
import Label from "glesys-controlpanel/components/label";
import { t, tKey } from "ember-intl";
import { and, not } from "ember-truth-helpers";
import Dropdown from "glesys-controlpanel/components/dropdown";
import DropdownItem from "glesys-controlpanel/components/dropdown/item";
import DropdownDestructiveItem from "glesys-controlpanel/components/dropdown/destructive-item";
import IconsMoreHorizontal from "glesys-controlpanel/components/icons/more-horizontal";
import { on } from "@ember/modifier";
import { task } from "ember-concurrency";
import modal from "glesys-controlpanel/helpers/modal";
import { saveRecord } from "@warp-drive/legacy/compat/builders";

@forbidExtraArgs
export default class EntityShipment extends Component {
  @service intl;
  @service store;

  @arg(instanceOf(ShipmentModel)) entity;

  @arg(bool) includeRemoteHandsRequest = true;

  get shipment() {
    return this.entity;
  }

  get tableData() {
    return [
      {
        header: this.intl.t("label.tracking-id"),
        value: this.shipment.trackingid ?? this.intl.t("shipments.no-tracking-number"),
      },
      {
        header: this.intl.t("label.data-center"),
        value: this.shipment.datacenter,
      },
      {
        header: this.intl.t("label.created"),
        value: dayjs(this.shipment.createdat).format("YYYY-MM-DD HH:mm"),
      },
    ];
  }

  get readableStatus() {
    const translationKeys = {
      REGISTERED: tKey("shipments.statuses.REGISTERED"),
      PENDING: tKey("shipments.statuses.PENDING"),
      RECEIVED: tKey("shipments.statuses.RECEIVED"),
      DELIVERED: tKey("shipments.statuses.DELIVERED"),
      CANCELED: tKey("shipments.statuses.CANCELED"),
    };

    if (!translationKeys[this.shipment.status]) {
      return this.intl.t("shipments.statuses.UNKNOWN");
    }

    return this.intl.t(translationKeys[this.shipment.status]);
  }

  cancelShipmentTask = task(async () => {
    this.shipment.status = "CANCELED";

    await this.store.request(saveRecord(this.shipment));
  });

  editShipmentTask = task(async (trackingId) => {
    this.shipment.trackingid = trackingId;

    try {
      await this.store.request(saveRecord(this.shipment, { adapterOptions: { action: "edit" } }));
    } catch (error) {
      this.shipment.rollbackAttributes();
      throw error;
    }
  });

  <template>
    <Box data-test-shipment={{this.shipment.id}} @collapsable={{true}} ...attributes>
      <:header>
        <div class="flex items-center gap-4">
          <div class="p-2 border rounded-sm border-gray-200">
            <IconsPackage class="size-4" />
          </div>
          <span class="text-base text-black">
            {{this.shipment.id}}
          </span>
          {{#if this.shipment.isRegistered}}
            <Pill data-test-shipment-status-registered @text={{this.readableStatus}} />
          {{/if}}

          {{#if this.shipment.isPending}}
            <Pill data-test-shipment-status-pending @text={{this.readableStatus}} />
          {{/if}}

          {{#if this.shipment.isReceived}}
            <PillInfo data-test-shipment-status-received @text={{this.readableStatus}} />
          {{/if}}

          {{#if this.shipment.isDelivered}}
            <PillSuccess data-test-shipment-status-delivered @text={{this.readableStatus}} />
          {{/if}}

          {{#if this.shipment.isCanceled}}
            <PillError data-test-shipment-status-canceled @text={{this.readableStatus}} />
          {{/if}}
        </div>
      </:header>
      <:headerActions>
        <div
          class="w-9 h-8 mr-2 cursor-pointer flex items-center justify-center rounded-sm border border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50 hover:text-gray-800 ml-2"
        >
          <Dropdown
            aria-label={{t "label.action-menu-aria-label" name=this.shipment.id}}
            data-test-select-menu
            @chevron={{false}}
          >
            <:header>
              <IconsMoreHorizontal class="w-12" />
            </:header>
            <:default>
              <DropdownItem
                data-test-shipment-edit-tracking-number
                disabled={{not this.shipment.isEditable}}
                {{on "click" (modal "modals/edit-shipment" shipment=this.shipment submitTask=this.editShipmentTask)}}
              >
                {{t "shipments.edit-tracking-number"}}
              </DropdownItem>
              <DropdownDestructiveItem
                data-test-shipment-cancel
                disabled={{this.shipment.isNotCancelable}}
                {{on
                  "click"
                  (modal
                    "modals/confirm"
                    task=this.cancelShipmentTask
                    titleText=(t "shipments.cancel.title" shipmentId=this.shipment.id)
                    text=(t "shipments.cancel.text")
                    buttonText=(t "shipments.cancel.button")
                  )
                }}
              >
                {{t "button.cancel"}}
              </DropdownDestructiveItem>
            </:default>
          </Dropdown>
        </div>
      </:headerActions>
      <:default>
        <EntityTable @entity={{this.tableData}} />

        {{#if (and this.includeRemoteHandsRequest this.shipment.remoteHandsRequest)}}
          <Label class="pt-6">{{t "label.remote-hands-request"}}</Label>
          <EntityRemoteHandsRequest @entity={{this.shipment.remoteHandsRequest}} @includeShipments={{false}} />
        {{/if}}
      </:default>
    </Box>
  </template>
}
